import {
  BffTrimData,
  PayloadPeopleValues,
  RangeSimRimData,
  RangeSimRimId,
  RangeTenantConfiguration,
  RangeTireSeasons,
  RangeTrimModel,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {
  CommonFeatureServices,
  formatModelSelectId,
  formatTrimSelectId,
  FormField,
  getSelectedModel,
  getSelectedTrim,
  getTrimAndVariantForModelId,
  RangeFeatureAppContentConfig,
  TireTypes,
  TrackingEvent,
  useConfigurationsContext,
  useFeatureAppId,
  useFeatureService,
  useFormField,
  useTrackingFunctions,
  YesNoValues,
  getDefaultDrivingProfile,
  useGetAppDataContent,
  getDefaultOutsideTemperature,
  DrivingProfileVector,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import React from 'react';
import {useTenantConfiguration} from './use-tenant-configuration';
import {useTranslation} from './use-translation';
import {useTrims} from './use-trims';
import {Logger} from '@feature-hub/core';

export interface RangeCalculationFormState {
  /**
   * Set of weightings of different driving environments in percent
   * [city, country, motorway]
   */
  drivingProfile: DrivingProfileVector;
  /** Does person drive faster than 120 km/h on the highway */
  fasterHighway: YesNoValues;
  /** Outside temperature in °C */
  outsideTemperature: number;
  /** Is air conditioning (or heating) on? */
  airConditioning: YesNoValues;
  /** Payload weight as "1 or multiple persons" in the car */
  payloadPeople: PayloadPeopleValues;
  /** Payload weight in kilogram */
  payloadWeight: number;
  /**  Which tires are on the car */
  tireType: TireTypes;
  /**  Which rim / tire size is selected */
  rim: RangeSimRimId;
  /**  Which model type / "trim "is selected */
  modelTypeId: string;
  /**  Which exact model / engine is selected */
  modelEngineId: string;
}

export type RangeCalculationFormFields = {
  [Field in keyof RangeCalculationFormState]: FormField<
    RangeCalculationFormState[Field]
  >;
};

export interface RangeCalculationForm {
  formFields: RangeCalculationFormFields;
  formState: RangeCalculationFormState;
  selectedTrim: BffTrimData<RangeTrimModel>;
  selectedModel: RangeTrimModel;
  selectedRim: RangeSimRimData;
}

const getDefaultState = (
  trim: BffTrimData<RangeTrimModel>,
  tenantConfiguration: RangeTenantConfiguration,
  appDataContent?: RangeFeatureAppContentConfig,
  logger?: Logger
): RangeCalculationFormState => {
  const outsideTemperature = getDefaultOutsideTemperature(
    appDataContent?.temperature,
    tenantConfiguration?.inputSection?.temperatureQuestion?.range?.default,
    logger
  );
  return {
    drivingProfile: getDefaultDrivingProfile(
      appDataContent?.drivingLocation,
      undefined,
      logger
    ),
    fasterHighway: YesNoValues.no,
    outsideTemperature,
    airConditioning: YesNoValues.no,
    payloadPeople:
      appDataContent?.passengers ??
      tenantConfiguration?.inputSection?.passengersQuestion.default ??
      PayloadPeopleValues.one,
    payloadWeight: 0,
    ...getDefaultValuesForTrim(
      trim,
      outsideTemperature,
      tenantConfiguration.featureFlags?.autoTireSelection
    ),
  };
};

const getDefaultValuesForTrim = (
  trim: BffTrimData<RangeTrimModel>,
  outsideTemperature?: number,
  autoTireSelection?: boolean
): Pick<
  RangeCalculationFormState,
  'modelEngineId' | 'modelTypeId' | 'rim' | 'tireType'
> => {
  const modelTypeId = formatTrimSelectId(trim);
  const firstModel = trim.modelVariants[0];

  return {
    modelTypeId,
    ...getDefaultValuesForModel(
      firstModel,
      outsideTemperature,
      autoTireSelection
    ),
  };
};

const getDefaultValuesForModel = (
  model: RangeTrimModel,
  outsideTemperature?: number,
  autoTireSelection?: boolean,
  nextTireType?: TireTypes
): Pick<RangeCalculationFormState, 'modelEngineId' | 'rim' | 'tireType'> => {
  const tireType =
    nextTireType ?? getTireType(outsideTemperature, autoTireSelection);

  const modelEngineId = formatModelSelectId(model);

  return {
    modelEngineId,
    tireType,
    rim: getDefaultRimId(model, tireType),
  };
};

const getTireType = (
  outsideTemperature: number | undefined,
  autoTireSelection: boolean | undefined,
  currentTireType?: TireTypes
): TireTypes => {
  if (!autoTireSelection || currentTireType === TireTypes.allSeason) {
    return currentTireType || TireTypes.summer;
  }

  return outsideTemperature !== undefined
    ? getOutsideTemperatureTireType(outsideTemperature)
    : TireTypes.summer;
};

const getDefaultRimId = (
  model: RangeTrimModel,
  season: TireTypes
): RangeSimRimId => {
  // Models are validated to always have simulation constants and at least one rim with summer/winter tires.
  // rim is currently hardcoded to R1 for commercial vehicles
  const rims = model.simulationConstants.rims;
  const rim = Object.entries(rims).find(
    ([_, r]) => r.season === (season as string as RangeTireSeasons)
  )?.[0] as RangeSimRimId | undefined;

  if (!rim) {
    throw new Error(`Model is missing ${season} rims.`);
  }

  return rim;
};

const getOutsideTemperatureTireType = (outsideTemperature: number) => {
  return outsideTemperature < 10 ? TireTypes.winter : TireTypes.summer;
};

const getSelectedRimData = (
  model: RangeTrimModel,
  rimId: RangeSimRimId
): RangeSimRimData => {
  const rimData = model.simulationConstants.rims[rimId];
  if (!rimData) {
    throw new Error('Impossible state - unavailable rim ID selected');
  }
  return rimData;
};

/**
 * used to auto-set the air conditioning toggle, unless the user set the toggle
 * explicitly
 */
const guessAirConValue = (temp: number) =>
  temp <= 0 || temp > 20 ? YesNoValues.yes : YesNoValues.no;

export function useRangeCalculationForm(): RangeCalculationForm {
  const baseId = useFeatureAppId();
  const trims = useTrims();
  const layerButtonCustomChannel = useFeatureService(
    CommonFeatureServices.layerButtonChanelService
  );

  const tenantConfiguration = useTenantConfiguration();
  const translations = useTranslation();
  const logger = useFeatureService(CommonFeatureServices.s2Logger);
  const tracking = useTrackingFunctions();
  const appDataContent = useGetAppDataContent<RangeFeatureAppContentConfig>(
    useConfigurationsContext().appDataContent
  );
  const trimForModelId =
    appDataContent &&
    appDataContent.defaultModelId &&
    appDataContent.defaultModelId.length > 0 &&
    getTrimAndVariantForModelId(trims, appDataContent.defaultModelId);

  const defaultState = getDefaultState(
    trimForModelId || trims[0],
    tenantConfiguration,
    appDataContent,
    logger
  );

  // use refs here to be able to reference these within onChange side effects
  const formFieldsRef = React.useRef<RangeCalculationFormFields | null>(null);
  const selectedTrimRef = React.useRef<BffTrimData<RangeTrimModel>>(trims[0]);
  const selectedModelRef = React.useRef<RangeTrimModel>(
    trims[0].modelVariants[0]
  );

  const drivingProfile = useFormField(
    `${baseId}-driving-profile`,
    defaultState.drivingProfile,
    translations.inputSection.drivingLocationsQuestion.heading,
    React.useCallback(
      (newValue: DrivingProfileVector) =>
        tracking[TrackingEvent.DrivingProfileChange]?.({
          locationPercentages: newValue,
        }),
      [tracking]
    )
  );

  const fasterHighway = useFormField(
    `${baseId}-faster-highway`,
    defaultState.fasterHighway,
    translations.inputSection.highwaySpeedQuestion.heading,
    React.useCallback(
      (value: YesNoValues) =>
        tracking[TrackingEvent.HighwayToggleChange]?.({value}),
      [tracking]
    )
  );

  /** Has air conditioning toggle been set by the user already? */
  const airConditioningTouched = React.useRef(false);

  const outsideTemperature = useFormField(
    `${baseId}-outside-temperature`,
    defaultState.outsideTemperature,
    translations.inputSection.temperatureQuestion.heading,
    React.useCallback(
      (newValue: number) => {
        if (
          !airConditioningTouched.current &&
          tenantConfiguration.featureFlags?.autoToggleAirConToggle
        ) {
          formFieldsRef.current?.airConditioning.onChangeWithoutSideEffect(
            guessAirConValue(newValue)
          );
        }

        const autoTireSelection =
          tenantConfiguration.featureFlags?.autoTireSelection;
        if (autoTireSelection) {
          const selectedTireType = formFieldsRef.current?.tireType.value;
          const nextTireType = getTireType(
            newValue,
            autoTireSelection,
            selectedTireType
          );
          if (
            selectedTireType !== TireTypes.allSeason &&
            selectedTireType !== nextTireType
          ) {
            const next = getDefaultValuesForModel(
              selectedModelRef.current,
              newValue,
              autoTireSelection,
              nextTireType
            );
            formFieldsRef.current?.rim.onChangeWithoutSideEffect(next.rim);
            formFieldsRef.current?.tireType.onChangeWithoutSideEffect(
              next.tireType
            );
          }
        }
        tracking[TrackingEvent.OutsideTemperatureChange]?.({
          temperatureCelsius: newValue,
        });
      },
      [airConditioningTouched, formFieldsRef, tenantConfiguration, tracking]
    )
  );

  const airConditioning = useFormField(
    `${baseId}-air-conditioning`,
    defaultState.airConditioning,
    translations.inputSection.airConditionQuestion.heading,
    React.useCallback(
      (value: YesNoValues) => {
        airConditioningTouched.current = true;
        tracking[TrackingEvent.HacToggleChange]?.({value});
      },
      [airConditioningTouched, tracking]
    )
  );

  const payloadPeople = useFormField(
    `${baseId}-payload-people`,
    defaultState.payloadPeople,
    translations.inputSection.passengersQuestion.heading,
    React.useCallback(
      (value: PayloadPeopleValues) =>
        tracking[TrackingEvent.PayloadToggleChange]?.({value}),
      [tracking]
    )
  );

  const payloadWeight = useFormField(
    `${baseId}-payload-weight`,
    defaultState.payloadWeight,
    translations.inputSection.payloadQuestion.heading,
    React.useCallback(
      (newValue: number) =>
        tracking[TrackingEvent.ExactPayloadChange]?.({
          payloadKg: newValue,
        }),
      [tracking]
    )
  );

  const tireType = useFormField(
    `${baseId}-which-tires`,
    defaultState.tireType,
    translations.inputSection.tireSelectionOptions?.tireLabel ||
      translations.carSelectionSection.tireSelection?.tireLabel ||
      '',
    React.useCallback(
      (newValue: TireTypes) => {
        const newDefaultRimId = getDefaultRimId(
          selectedModelRef.current,
          newValue
        );
        // calculate new default rim
        formFieldsRef.current?.rim.onChangeWithoutSideEffect(newDefaultRimId);
        const rimData = getSelectedRimData(
          selectedModelRef.current,
          newDefaultRimId
        );

        tracking[TrackingEvent.RangeTireTypeSelect]?.({
          tireType: newValue,
          rimId: newDefaultRimId,
          rim: rimData,
        });
      },
      [formFieldsRef, selectedModelRef, tracking]
    )
  );

  const rim = useFormField(
    `${baseId}-tire-size`,
    defaultState.rim,
    translations.inputSection.tireSelectionOptions?.rimLabel ||
      translations.carSelectionSection.tireSelection?.rimLabel ||
      '',
    React.useCallback(
      (newValue: RangeSimRimId) => {
        const rimData = getSelectedRimData(selectedModelRef.current, newValue);

        tracking[TrackingEvent.RangeRimSelect]?.({
          rimId: newValue,
          rim: rimData,
        });
      },
      [selectedModelRef, tracking]
    )
  );

  const modelTypeId = useFormField(
    `${baseId}-trim-id`,
    defaultState.modelTypeId,
    translations.carSelectionSection.modelAndMotorSelection.modelLabel,
    React.useCallback(
      (newValue: string, shared?: boolean) => {
        const nextSelectedTrim = getSelectedTrim<RangeTrimModel>(
          trims,
          newValue
        );

        const next = getDefaultValuesForTrim(
          nextSelectedTrim,
          // TODO: check if we should change the tire size after changing the model
          formFieldsRef.current?.outsideTemperature.value,
          tenantConfiguration.featureFlags?.autoTireSelection
        );
        const nextSelectedModel = getSelectedModel<RangeTrimModel>(
          nextSelectedTrim,
          next.modelEngineId
        );

        const nextRimData = getSelectedRimData(nextSelectedModel, next.rim);

        formFieldsRef.current?.rim.onChangeWithoutSideEffect(next.rim);
        formFieldsRef.current?.tireType.onChangeWithoutSideEffect(
          next.tireType
        );
        selectedTrimRef.current = nextSelectedTrim;
        formFieldsRef.current?.modelEngineId.onChangeWithoutSideEffect(
          next.modelEngineId
        );

        if (layerButtonCustomChannel) {
          layerButtonCustomChannel.client.set({
            modelTypeId: next.modelTypeId,
          });
        }

        if (!shared) {
          tracking[TrackingEvent.RangeTrimSelect]?.({
            trim: nextSelectedTrim,
            model: nextSelectedModel,
            tireType: next.tireType,
            rimId: next.rim,
            rim: nextRimData,
          });
        }
      },
      [formFieldsRef, tracking, trims]
    )
  );

  const modelEngineId = useFormField(
    `${baseId}-engine-id`,
    defaultState.modelEngineId,
    translations.carSelectionSection.modelAndMotorSelection.motorLabel,
    React.useCallback(
      (newValue: string) => {
        const nextSelectedModel = getSelectedModel<RangeTrimModel>(
          selectedTrimRef.current,
          newValue
        );
        const next = getDefaultValuesForModel(
          nextSelectedModel,
          // TODO: check if we should change the tire size after changing the model
          formFieldsRef.current?.outsideTemperature.value,
          tenantConfiguration.featureFlags?.autoTireSelection
        );
        const nextRimData = getSelectedRimData(nextSelectedModel, next.rim);

        if (nextSelectedModel.engine) {
          tracking[TrackingEvent.RangeEngineSelect]?.({
            model: nextSelectedModel,
            tireType: next.tireType,
            rimId: next.rim,
            rim: nextRimData,
          });
        }

        formFieldsRef.current?.rim.onChangeWithoutSideEffect(next.rim);
        formFieldsRef.current?.tireType.onChangeWithoutSideEffect(
          next.tireType
        );
      },
      [selectedTrimRef, formFieldsRef, tracking]
    )
  );

  const selectedTrim = React.useMemo(
    () => getSelectedTrim<RangeTrimModel>(trims, modelTypeId.value),
    [trims, modelTypeId.value]
  );

  const selectedModel = React.useMemo(
    () => getSelectedModel<RangeTrimModel>(selectedTrim, modelEngineId.value),
    [selectedTrim, modelEngineId.value]
  );

  const selectedRim = React.useMemo(
    () => getSelectedRimData(selectedModel, rim.value),
    [selectedModel, rim.value]
  );

  const formFields: RangeCalculationFormFields = React.useMemo(
    () => ({
      drivingProfile,
      fasterHighway,
      outsideTemperature,
      airConditioning,
      payloadPeople,
      payloadWeight,
      tireType,
      rim,
      modelTypeId,
      modelEngineId,
    }),
    [
      drivingProfile,
      fasterHighway,
      outsideTemperature,
      airConditioning,
      payloadPeople,
      payloadWeight,
      tireType,
      rim,
      modelTypeId,
      modelEngineId,
    ]
  );

  const formState: RangeCalculationFormState = React.useMemo(
    () => ({
      drivingProfile: drivingProfile.value,
      fasterHighway: fasterHighway.value,
      outsideTemperature: outsideTemperature.value,
      airConditioning: airConditioning.value,
      payloadPeople: payloadPeople.value,
      payloadWeight: payloadWeight.value,
      tireType: tireType.value,
      rim: rim.value,
      modelTypeId: modelTypeId.value,
      modelEngineId: modelEngineId.value,
    }),
    [
      drivingProfile.value,
      fasterHighway.value,
      outsideTemperature.value,
      airConditioning.value,
      payloadPeople.value,
      payloadWeight.value,
      tireType.value,
      rim.value,
      modelTypeId.value,
      modelEngineId.value,
    ]
  );

  // Important: update the ref values so that side effects reference current data
  formFieldsRef.current = formFields;
  selectedTrimRef.current = selectedTrim;
  selectedModelRef.current = selectedModel;

  return {
    formFields,
    formState,
    selectedTrim,
    selectedModel,
    selectedRim,
  };
}
